import React, { useState, useRef } from 'react';
import { Modal, Button, Upload, message, Typography, Tooltip, Space, Select } from 'antd';
import { UploadOutlined, QuestionCircleOutlined, InboxOutlined } from '@ant-design/icons';
import api from '../../utils/api';

const { Paragraph } = Typography;
const { Dragger } = Upload;
const { Option } = Select;

const AssetImportModal = ({ visible, onCancel, onSuccess }) => {
    const [fileList, setFileList] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [importMode, setImportMode] = useState('update'); // 'update' or 'replace'
    const uploadRef = useRef();

    const handleBeforeUpload = (file) => {
        const isExcel = file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' || 
                      file.type === 'application/vnd.ms-excel' ||
                      file.name.endsWith('.xlsx') || 
                      file.name.endsWith('.xls');
        if (!isExcel) {
            message.error(`${file.name} is not a valid Excel file!`);
            return false;
        }
        
        // Clear previous file and set new one
        setFileList([file]);
        return false;
    };

    const handleRemove = () => {
        setFileList([]);
        if (uploadRef.current) {
            uploadRef.current.upload.abort();
        }
    };

const handleImport = async () => {
    if (fileList.length === 0) {
        message.warning('Please select an Excel file to upload.');
        return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', fileList[0]);
    formData.append('freshImport', importMode === 'replace');

    try {
        const response = await api.post('/api/assets/import', formData, {
            headers: { 
                'Content-Type': 'multipart/form-data',
                'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
        });
        
        let successMessage;
        if (response.data.added !== undefined && response.data.updated !== undefined) {
            successMessage = `Import successful! ${response.data.added} added, ${response.data.updated} updated.`;
        } else {
            successMessage = response.data.message || 'Import completed successfully';
        }
        
        message.success(successMessage);
        onSuccess(response.data);
        handleRemove();
    } catch (error) {
        console.error('Full error:', error);
        let errorMessage = 'Import failed. Please check the file and try again.';
        
        if (error.response) {
            // Server responded with error status
            console.error('Server error response:', error.response.data);
            errorMessage = error.response.data.message || errorMessage;
            
            if (error.response.data.error) {
                errorMessage += ` (${error.response.data.error})`;
            }
        } else if (error.request) {
            // Request was made but no response
            console.error('No response received:', error.request);
            errorMessage = 'No response from server. Please try again.';
        } else {
            // Something else happened
            console.error('Request setup error:', error.message);
            errorMessage = error.message || errorMessage;
        }
        
        message.error(errorMessage);
    } finally {
        setUploading(false);
    }
};

    return (
        <Modal
            title="Import Assets"
            visible={visible}
            onCancel={() => {
                handleRemove();
                onCancel();
            }}
            footer={[
                <Button key="cancel" onClick={() => {
                    handleRemove();
                    onCancel();
                }} disabled={uploading}>
                    Cancel
                </Button>,
                <Button
                    key="import"
                    type="primary"
                    onClick={handleImport}
                    loading={uploading}
                    disabled={fileList.length === 0}
                    icon={<UploadOutlined />}
                >
                    {uploading ? 'Importing...' : 'Import Assets'}
                </Button>
            ]}
            width={650}
            destroyOnClose
        >
            <Paragraph>
                Upload Excel file with these columns:
                <br />
                <strong>ASSET TYPE, MODEL, SERIAL NO., IP ADDRESS, HOST NAME</strong>
                <Tooltip title="First row must contain headers. SERIAL NO. is used to identify duplicates">
                    <QuestionCircleOutlined style={{ marginLeft: 8 }} />
                </Tooltip>
            </Paragraph>

            <div style={{ marginBottom: 16 }}>
                <Space>
                    <span>Import Mode:</span>
                    <Select 
                        value={importMode}
                        onChange={setImportMode}
                        style={{ width: 200 }}
                        disabled={uploading}
                    >
                        <Option value="update">Update Existing (Default)</Option>
                        <Option value="replace">Replace All Assets</Option>
                    </Select>
                </Space>
                {importMode === 'replace' && (
                    <p style={{ color: '#ff4d4f', marginTop: 8 }}>
                        Warning: This will delete all existing assets before importing!
                    </p>
                )}
            </div>

            <Dragger
                ref={uploadRef}
                name="file"
                accept=".xlsx,.xls,.csv"
                fileList={fileList}
                beforeUpload={handleBeforeUpload}
                onRemove={handleRemove}
                maxCount={1}
            >
                <p className="ant-upload-drag-icon">
                    <InboxOutlined />
                </p>
                <p className="ant-upload-text">Click or drag file here</p>
                <p className="ant-upload-hint">
                    Supports Excel files (.xlsx, .xls). {importMode === 'update' ? 
                    'Existing assets will be updated.' : 'All existing assets will be replaced.'}
                </p>
            </Dragger>

            {fileList.length > 0 && (
                <div style={{ marginTop: 16 }}>
                    <p>Ready to import: <strong>{fileList[0].name}</strong></p>
                    <p style={{ color: '#faad14' }}>
                        {importMode === 'update' ? 
                            'Note: Matching entries will be updated based on SERIAL NO.' : 
                            'Note: This will replace ALL existing assets with the imported data'}
                    </p>
                </div>
            )}
        </Modal>
    );
};

export default AssetImportModal;